import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Palette, Type, Eye, EyeOff, FileText, RotateCcw, Save, Settings2, Tag } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PDFSettingsPage = () => {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/pdf`);
      setSettings(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle impostazioni');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/pdf`, settings);
      toast.success('Impostazioni salvate!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Ripristinare le impostazioni predefinite?')) return;
    setSaving(true);
    try {
      const response = await axios.post(`${API}/settings/pdf/reset`);
      setSettings(response.data);
      toast.success('Impostazioni ripristinate!');
    } catch (error) {
      toast.error('Errore nel ripristino');
    } finally {
      setSaving(false);
    }
  };

  const updateNestedSetting = (section, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  if (!isAdmin()) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-500">Accesso riservato agli amministratori</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B6B93]"></div>
      </div>
    );
  }

  if (!settings) return null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="pdf-settings-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
            <Settings2 className="w-8 h-8 text-[#1B6B93]" />
            Personalizzazione PDF
          </h1>
          <p className="text-slate-500 mt-1">Personalizza colori, etichette e campi visibili nei PDF generati</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleReset} disabled={saving}>
            <RotateCcw className="w-4 h-4 mr-2" />Ripristina Default
          </Button>
          <Button className="btn-primary" onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />{saving ? 'Salvataggio...' : 'Salva Modifiche'}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="colors" className="w-full">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
          <TabsTrigger value="colors" className="flex items-center gap-2">
            <Palette className="w-4 h-4" />
            <span className="hidden sm:inline">Colori</span>
          </TabsTrigger>
          <TabsTrigger value="fonts" className="flex items-center gap-2">
            <Type className="w-4 h-4" />
            <span className="hidden sm:inline">Font</span>
          </TabsTrigger>
          <TabsTrigger value="fields" className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            <span className="hidden sm:inline">Campi</span>
          </TabsTrigger>
          <TabsTrigger value="labels" className="flex items-center gap-2">
            <Tag className="w-4 h-4" />
            <span className="hidden sm:inline">Etichette</span>
          </TabsTrigger>
        </TabsList>

        {/* COLORI */}
        <TabsContent value="colors" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5 text-[#1B6B93]" />
                Palette Colori
              </CardTitle>
              <CardDescription>Personalizza i colori utilizzati nei PDF</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <ColorPicker
                label="Colore Principale"
                description="Header, titoli, bordi"
                value={settings.colors?.primary || '#1B6B93'}
                onChange={(v) => updateNestedSetting('colors', 'primary', v)}
              />
              <ColorPicker
                label="Colore Secondario"
                description="Sottotitoli, elementi secondari"
                value={settings.colors?.secondary || '#4BA3C7'}
                onChange={(v) => updateNestedSetting('colors', 'secondary', v)}
              />
              <ColorPicker
                label="Colore Accento"
                description="Evidenziazioni, badge"
                value={settings.colors?.accent || '#A8D8EA'}
                onChange={(v) => updateNestedSetting('colors', 'accent', v)}
              />
              <ColorPicker
                label="Sfondo Header Tabelle"
                description="Background intestazioni"
                value={settings.colors?.header_bg || '#1B6B93'}
                onChange={(v) => updateNestedSetting('colors', 'header_bg', v)}
              />
              <ColorPicker
                label="Testo Header Tabelle"
                description="Colore testo intestazioni"
                value={settings.colors?.header_text || '#FFFFFF'}
                onChange={(v) => updateNestedSetting('colors', 'header_text', v)}
              />
              <ColorPicker
                label="Righe Alternate"
                description="Sfondo righe pari tabelle"
                value={settings.colors?.row_alternate || '#F8FAFC'}
                onChange={(v) => updateNestedSetting('colors', 'row_alternate', v)}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* FONT */}
        <TabsContent value="fonts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Type className="w-5 h-5 text-[#1B6B93]" />
                Dimensioni Testo
              </CardTitle>
              <CardDescription>Regola le dimensioni dei caratteri nei PDF</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="space-y-2">
                <Label>Titoli (pt)</Label>
                <Input
                  type="number"
                  min="8"
                  max="24"
                  value={settings.fonts?.title_size || 14}
                  onChange={(e) => updateNestedSetting('fonts', 'title_size', parseInt(e.target.value))}
                  className="bg-white"
                />
                <p className="text-xs text-slate-500">Intestazioni principali</p>
              </div>
              <div className="space-y-2">
                <Label>Header (pt)</Label>
                <Input
                  type="number"
                  min="6"
                  max="18"
                  value={settings.fonts?.header_size || 10}
                  onChange={(e) => updateNestedSetting('fonts', 'header_size', parseInt(e.target.value))}
                  className="bg-white"
                />
                <p className="text-xs text-slate-500">Sezioni e header tabelle</p>
              </div>
              <div className="space-y-2">
                <Label>Corpo (pt)</Label>
                <Input
                  type="number"
                  min="6"
                  max="14"
                  value={settings.fonts?.body_size || 8}
                  onChange={(e) => updateNestedSetting('fonts', 'body_size', parseInt(e.target.value))}
                  className="bg-white"
                />
                <p className="text-xs text-slate-500">Testo principale</p>
              </div>
              <div className="space-y-2">
                <Label>Note (pt)</Label>
                <Input
                  type="number"
                  min="5"
                  max="12"
                  value={settings.fonts?.small_size || 7}
                  onChange={(e) => updateNestedSetting('fonts', 'small_size', parseInt(e.target.value))}
                  className="bg-white"
                />
                <p className="text-xs text-slate-500">Footer e note piccole</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CAMPI VISIBILI */}
        <TabsContent value="fields" className="space-y-4">
          {/* Rapporti Singoli */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#1B6B93]" />
                Rapporti Singoli/Multigiorno
              </CardTitle>
              <CardDescription>Scegli quali sezioni mostrare nei PDF dei rapporti</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <FieldToggle
                label="Numero Rapporto"
                checked={settings.report_fields?.show_report_number !== false}
                onChange={(v) => updateNestedSetting('report_fields', 'show_report_number', v)}
              />
              <FieldToggle
                label="Data"
                checked={settings.report_fields?.show_date !== false}
                onChange={(v) => updateNestedSetting('report_fields', 'show_date', v)}
              />
              <FieldToggle
                label="Info Cantiere"
                checked={settings.report_fields?.show_site_info !== false}
                onChange={(v) => updateNestedSetting('report_fields', 'show_site_info', v)}
              />
              <FieldToggle
                label="Info Cliente"
                checked={settings.report_fields?.show_client_info !== false}
                onChange={(v) => updateNestedSetting('report_fields', 'show_client_info', v)}
              />
              <FieldToggle
                label="Elenco Dipendenti"
                checked={settings.report_fields?.show_employees !== false}
                onChange={(v) => updateNestedSetting('report_fields', 'show_employees', v)}
              />
              <FieldToggle
                label="Attrezzature"
                checked={settings.report_fields?.show_equipment !== false}
                onChange={(v) => updateNestedSetting('report_fields', 'show_equipment', v)}
              />
              <FieldToggle
                label="Distinta Ore"
                description="Diurne, notturne, semifestive, festive"
                checked={settings.report_fields?.show_hours_detail !== false}
                onChange={(v) => updateNestedSetting('report_fields', 'show_hours_detail', v)}
              />
              <FieldToggle
                label="Riferimento/Ticket"
                checked={settings.report_fields?.show_ticket !== false}
                onChange={(v) => updateNestedSetting('report_fields', 'show_ticket', v)}
              />
              <FieldToggle
                label="Note"
                checked={settings.report_fields?.show_notes !== false}
                onChange={(v) => updateNestedSetting('report_fields', 'show_notes', v)}
              />
              <FieldToggle
                label="Firme"
                checked={settings.report_fields?.show_signatures !== false}
                onChange={(v) => updateNestedSetting('report_fields', 'show_signatures', v)}
              />
            </CardContent>
          </Card>

          {/* Report Fatture */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" />
                Report Fatture
              </CardTitle>
              <CardDescription>Colonne visibili nel report aggregato fatture</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <FieldToggle
                label="Numero Rapporto"
                checked={settings.invoice_fields?.show_report_number !== false}
                onChange={(v) => updateNestedSetting('invoice_fields', 'show_report_number', v)}
              />
              <FieldToggle
                label="Data Rapporto"
                checked={settings.invoice_fields?.show_report_date !== false}
                onChange={(v) => updateNestedSetting('invoice_fields', 'show_report_date', v)}
              />
              <FieldToggle
                label="Date Intervento"
                checked={settings.invoice_fields?.show_intervention_date !== false}
                onChange={(v) => updateNestedSetting('invoice_fields', 'show_intervention_date', v)}
              />
              <FieldToggle
                label="Nome Cliente"
                checked={settings.invoice_fields?.show_client_name !== false}
                onChange={(v) => updateNestedSetting('invoice_fields', 'show_client_name', v)}
              />
              <FieldToggle
                label="Nome Cantiere"
                checked={settings.invoice_fields?.show_site_name !== false}
                onChange={(v) => updateNestedSetting('invoice_fields', 'show_site_name', v)}
              />
              <FieldToggle
                label="Ore Lavorate"
                checked={settings.invoice_fields?.show_work_hours !== false}
                onChange={(v) => updateNestedSetting('invoice_fields', 'show_work_hours', v)}
              />
              <FieldToggle
                label="Ore Attrezzature"
                checked={settings.invoice_fields?.show_equipment_hours !== false}
                onChange={(v) => updateNestedSetting('invoice_fields', 'show_equipment_hours', v)}
              />
              <FieldToggle
                label="Riferimento"
                checked={settings.invoice_fields?.show_ticket !== false}
                onChange={(v) => updateNestedSetting('invoice_fields', 'show_ticket', v)}
              />
            </CardContent>
          </Card>

          {/* Report Paghe */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                Report Paghe
              </CardTitle>
              <CardDescription>Colonne visibili nel report aggregato paghe</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <FieldToggle
                label="Numero Rapporto"
                checked={settings.payroll_fields?.show_report_number !== false}
                onChange={(v) => updateNestedSetting('payroll_fields', 'show_report_number', v)}
              />
              <FieldToggle
                label="Data"
                checked={settings.payroll_fields?.show_date !== false}
                onChange={(v) => updateNestedSetting('payroll_fields', 'show_date', v)}
              />
              <FieldToggle
                label="Nome Dipendente"
                checked={settings.payroll_fields?.show_employee_name !== false}
                onChange={(v) => updateNestedSetting('payroll_fields', 'show_employee_name', v)}
              />
              <FieldToggle
                label="Nome Cantiere"
                checked={settings.payroll_fields?.show_site_name !== false}
                onChange={(v) => updateNestedSetting('payroll_fields', 'show_site_name', v)}
              />
              <FieldToggle
                label="Distinta Ore"
                checked={settings.payroll_fields?.show_hours_detail !== false}
                onChange={(v) => updateNestedSetting('payroll_fields', 'show_hours_detail', v)}
              />
              <FieldToggle
                label="Riferimento"
                checked={settings.payroll_fields?.show_ticket !== false}
                onChange={(v) => updateNestedSetting('payroll_fields', 'show_ticket', v)}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ETICHETTE */}
        <TabsContent value="labels" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Tag className="w-5 h-5 text-[#1B6B93]" />
                Titoli e Intestazioni
              </CardTitle>
              <CardDescription>Personalizza i titoli delle sezioni nei PDF</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <LabelInput
                label="Titolo Rapporto"
                value={settings.labels?.report_title || 'RAPPORTO LAVORO STRAORDINARIO'}
                onChange={(v) => updateNestedSetting('labels', 'report_title', v)}
              />
              <LabelInput
                label="Titolo Multi-giorno"
                value={settings.labels?.multi_day_title || 'RAPPORTO LAVORO STRAORDINARIO MULTI-GIORNO'}
                onChange={(v) => updateNestedSetting('labels', 'multi_day_title', v)}
              />
              <LabelInput
                label="Sezione Dipendenti"
                value={settings.labels?.employees_section || 'DIPENDENTI COINVOLTI'}
                onChange={(v) => updateNestedSetting('labels', 'employees_section', v)}
              />
              <LabelInput
                label="Sezione Attrezzature"
                value={settings.labels?.equipment_section || 'ATTREZZATURE UTILIZZATE'}
                onChange={(v) => updateNestedSetting('labels', 'equipment_section', v)}
              />
              <LabelInput
                label="Sezione Firme"
                value={settings.labels?.signatures_section || 'FIRME'}
                onChange={(v) => updateNestedSetting('labels', 'signatures_section', v)}
              />
              <LabelInput
                label="Titolo Report Fatture"
                value={settings.labels?.invoice_title || 'REPORT FATTURE'}
                onChange={(v) => updateNestedSetting('labels', 'invoice_title', v)}
              />
              <LabelInput
                label="Titolo Report Paghe"
                value={settings.labels?.payroll_title || 'REPORT PAGHE'}
                onChange={(v) => updateNestedSetting('labels', 'payroll_title', v)}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Tag className="w-5 h-5 text-[#4BA3C7]" />
                Etichette Campi
              </CardTitle>
              <CardDescription>Personalizza le etichette dei singoli campi</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <LabelInput
                label="Data"
                value={settings.labels?.label_date || 'Data:'}
                onChange={(v) => updateNestedSetting('labels', 'label_date', v)}
              />
              <LabelInput
                label="Cantiere"
                value={settings.labels?.label_site || 'Cantiere:'}
                onChange={(v) => updateNestedSetting('labels', 'label_site', v)}
              />
              <LabelInput
                label="Cliente"
                value={settings.labels?.label_client || 'Cliente:'}
                onChange={(v) => updateNestedSetting('labels', 'label_client', v)}
              />
              <LabelInput
                label="Indirizzo"
                value={settings.labels?.label_address || 'Indirizzo:'}
                onChange={(v) => updateNestedSetting('labels', 'label_address', v)}
              />
              <LabelInput
                label="Orario"
                value={settings.labels?.label_time || 'Orario:'}
                onChange={(v) => updateNestedSetting('labels', 'label_time', v)}
              />
              <LabelInput
                label="Ore Totali"
                value={settings.labels?.label_total_hours || 'ORE TOTALI:'}
                onChange={(v) => updateNestedSetting('labels', 'label_total_hours', v)}
              />
              <LabelInput
                label="Attivita"
                value={settings.labels?.label_activity || 'Attivita:'}
                onChange={(v) => updateNestedSetting('labels', 'label_activity', v)}
              />
              <LabelInput
                label="Note"
                value={settings.labels?.label_notes || 'Note:'}
                onChange={(v) => updateNestedSetting('labels', 'label_notes', v)}
              />
              <LabelInput
                label="Riferimento"
                value={settings.labels?.label_ticket || 'Ns./Vs. Rif.:'}
                onChange={(v) => updateNestedSetting('labels', 'label_ticket', v)}
              />
              <LabelInput
                label="Ore Diurne"
                value={settings.labels?.label_day_hours || 'Diurne'}
                onChange={(v) => updateNestedSetting('labels', 'label_day_hours', v)}
              />
              <LabelInput
                label="Ore Notturne"
                value={settings.labels?.label_night_hours || 'Notturne'}
                onChange={(v) => updateNestedSetting('labels', 'label_night_hours', v)}
              />
              <LabelInput
                label="Ore Semifestive"
                value={settings.labels?.label_semifestive_hours || 'Semifestive'}
                onChange={(v) => updateNestedSetting('labels', 'label_semifestive_hours', v)}
              />
              <LabelInput
                label="Ore Festive"
                value={settings.labels?.label_festive_hours || 'Festive'}
                onChange={(v) => updateNestedSetting('labels', 'label_festive_hours', v)}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Componente ColorPicker
const ColorPicker = ({ label, description, value, onChange }) => (
  <div className="space-y-2">
    <Label>{label}</Label>
    <div className="flex items-center gap-3">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-12 h-10 rounded cursor-pointer border border-slate-200"
      />
      <Input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-white font-mono text-sm flex-1"
        placeholder="#000000"
      />
    </div>
    {description && <p className="text-xs text-slate-500">{description}</p>}
  </div>
);

// Componente FieldToggle
const FieldToggle = ({ label, description, checked, onChange }) => (
  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
    <div className="space-y-0.5">
      <Label className="text-sm font-medium">{label}</Label>
      {description && <p className="text-xs text-slate-500">{description}</p>}
    </div>
    <Switch checked={checked} onCheckedChange={onChange} />
  </div>
);

// Componente LabelInput
const LabelInput = ({ label, value, onChange }) => (
  <div className="space-y-1">
    <Label className="text-xs text-slate-600">{label}</Label>
    <Input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-white h-9 text-sm"
    />
  </div>
);

export default PDFSettingsPage;
